import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import {
    useAddPresignedVideoUrlMutation,
    useAddVideoThumbnailMutation,
    useUploadVideoMutation,
} from '../../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../../shared/AuthRoles';
import { generateThumbnailFromVideo } from '../../../../../shared/utils';
import FormUpload from '../../../../shared/FormUpload';
import toast from '../../../../shared/Toast';
import { FormValues } from '../../../../types/experience';
import { Media } from '../../../../types/user';
import { useFormState } from '../useFormState';

const ExperienceVideos = () => {
    const loggedInUser = UserRole();
    const { isSaveDisabled, setIsSaveDisabled } = useFormState();
    const [addVideoThumbnail] = useAddVideoThumbnailMutation();
    const [getPresignedUrl] = useAddPresignedVideoUrlMutation();
    const [uploadVideo] = useUploadVideoMutation();
    const { control } = useFormContext<FormValues>();

    const videos = useWatch({ control, name: 'videos' });

    const handleVideos = async (files: File[]) => {
        if (!files.length) return;

        setIsSaveDisabled(true);
        const ownerId = loggedInUser.company;
        const uploadedVideos: Media[] = [];

        try {
            for (const file of files) {
                // Step 1: Get presigned URL
                const presign = await getPresignedUrl({
                    ownerId,
                    fileName: file.name,
                    contentType: file.type,
                }).unwrap();

                // Step 2: Upload to S3
                const uploadRes = await fetch(presign.uploadUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': file.type },
                    body: file,
                });

                if (!uploadRes.ok) {
                    toast.error('S3 upload failed.');
                    continue;
                }

                // Step 3: Generate thumbnail from uploaded video
                const thumbnailBlob = await generateThumbnailFromVideo(
                    presign.fileUrl,
                );
                const formData = new FormData();
                formData.append(
                    'files',
                    thumbnailBlob,
                    `${presign.id}_thumb.jpg`,
                );

                // Step 4: Upload thumbnail and get URL
                const requestInfo = [ownerId, formData];
                const thumbnailResponse =
                    await addVideoThumbnail(requestInfo).unwrap();

                // Step 5: Confirm video upload with thumbnail URL
                await uploadVideo({
                    ownerId,
                    id: presign.id,
                    fileUrl: presign.fileUrl,
                    thumbnail: thumbnailResponse?.src,
                }).unwrap();

                // Step 6: Add to uploaded videos array
                uploadedVideos.push({
                    id: presign.id,
                    src: presign.fileUrl,
                    thumbnail: thumbnailResponse?.src,
                });
            }

            return uploadedVideos;
        } catch (err) {
            console.error(err);
            toast.error('Something went wrong.');
            return undefined;
        } finally {
            setIsSaveDisabled(false);
        }
    };

    return (
        <fieldset>
            <Stack spacing={2}>
                <div>
                    <Typography variant='h2' component='legend'>
                        Videos (optional)
                    </Typography>
                    <Typography sx={{ color: 'text.secondary' }} gutterBottom>
                        Showcase your experience with up to 10 videos!
                    </Typography>
                </div>
                <div>
                    <FormUpload
                        name='videos'
                        control={control}
                        onUpload={handleVideos}
                        showFiles={true}
                        disabled={videos?.length >= 10 || isSaveDisabled}
                        validation={{
                            acceptedTypes: {
                                'video/*': [
                                    '.mp4',
                                    '.mov',
                                    '.avi',
                                    '.mkv',
                                    '.webm',
                                    '.m4v',
                                ],
                            },
                            maxFileSize: 540 * 1024 * 1024, // 540MB
                            maxFiles: 10,
                            orientation: 'landscape',
                            resolution: { width: 1024, height: 683 },
                        }}
                        uploadText='Click to browse videos or drag and drop'
                        mediaType='video'
                    />
                </div>
            </Stack>
        </fieldset>
    );
};

export default ExperienceVideos;
